import { useEffect, useState } from 'react';
import { Alert, Button, Card, Col, Form, InputGroup, Row, Spinner } from 'react-bootstrap';
import TagInput from '../common/TagInput';
import AttachmentField from './AttachmentField';
import { patientApi } from '../../api';
import { formatAge, titleCase } from '../../utils/format';

const BLOOD_GROUPS = ['', 'A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];

// Readings taken at the desk. They belong to today's visit, not to the patient.
const EMPTY_VITALS = { bpSystolic: '', bpDiastolic: '', pulse: '', temperature: '', spo2: '' };

const EMPTY = {
  name: '',
  mobile: '',
  age: '',
  ageUnit: 'years',
  gender: '',
  address: '',
  city: '',
  weight: '',
  bloodGroup: '',
  allergies: [],
  chronicConditions: [],
  notes: '',
  reports: [],
};

/**
 * Registration / edit form. On a new registration it checks the mobile number
 * first and offers the existing record, so the same person is not entered twice.
 */
export default function PatientForm({
  initial,
  onSubmit,
  submitLabel = 'Register Patient',
  onCancel,
  onPickExisting,
  // Registration also opens today's visit, so it asks for the readings taken at
  // the desk. Editing an existing record has no visit to put them on.
  showVitals = false,
}) {
  const [form, setForm] = useState({ ...EMPTY, ...(initial || {}) });
  const [vitals, setVitals] = useState(EMPTY_VITALS);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [duplicates, setDuplicates] = useState([]);
  const [checking, setChecking] = useState(false);

  const isEdit = !!initial?._id;

  useEffect(() => {
    if (initial) setForm({ ...EMPTY, ...initial });
  }, [initial]);

  // Duplicate check runs while the receptionist is still typing the number.
  useEffect(() => {
    if (isEdit) return undefined;
    const mobile = form.mobile.trim();
    if (mobile.length < 10) {
      setDuplicates([]);
      return undefined;
    }

    setChecking(true);
    const timer = setTimeout(async () => {
      try {
        const res = await patientApi.checkMobile(mobile);
        setDuplicates(res.data || []);
      } catch {
        setDuplicates([]);
      } finally {
        setChecking(false);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [form.mobile, isEdit]);

  function set(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
    if (errors[field]) setErrors((e) => ({ ...e, [field]: undefined }));
  }

  function setVital(field, value) {
    setVitals((v) => ({ ...v, [field]: value }));
  }

  function validate() {
    const next = {};
    if (!form.name.trim()) next.name = 'Patient name is required';
    if (!/^[0-9+\- ]{10,15}$/.test(form.mobile.trim())) next.mobile = 'Enter a valid 10 digit mobile number';
    if (!form.gender) next.gender = 'Select a gender';
    if (form.age !== '' && (Number(form.age) < 0 || Number(form.age) > 130)) next.age = 'Enter a valid age';
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!validate()) return;

    setBusy(true);
    try {
      // Anything left blank is dropped rather than saved as a zero reading.
      const measured = Object.fromEntries(
        Object.entries(vitals)
          .filter(([, v]) => v !== '')
          .map(([k, v]) => [k, Number(v)])
          .filter(([, v]) => Number.isFinite(v)),
      );

      await onSubmit(
        {
          ...form,
          name: form.name.trim(),
          mobile: form.mobile.trim(),
          age: form.age === '' ? undefined : Number(form.age),
          weight: form.weight === '' ? undefined : Number(form.weight),
        },
        measured,
      );
    } catch {
      // The caller has already shown the reason as a toast. Swallowing it here
      // keeps the form open with everything typed still in place - and stops it
      // becoming an unhandled rejection, which React shows as a crash overlay.
    } finally {
      setBusy(false);
    }
  }

  return (
    <Form onSubmit={handleSubmit} noValidate>
      {!isEdit && duplicates.length > 0 && (
        <Alert variant="warning">
          <div className="fw-semibold mb-2">
            <i className="bi bi-exclamation-triangle me-2" />
            This mobile number is already registered
          </div>
          {duplicates.map((p) => (
            <div
              key={p._id}
              className="d-flex flex-wrap justify-content-between align-items-center gap-2 border-top pt-2 mt-2"
            >
              <div className="small">
                <strong>{p.name}</strong> &middot; {p.patientId} &middot; {titleCase(p.gender)}, {formatAge(p)}
                <div className="text-muted">
                  {p.totalVisits} previous visit{p.totalVisits === 1 ? '' : 's'}
                </div>
              </div>
              {onPickExisting && (
                <Button size="sm" variant="warning" onClick={() => onPickExisting(p)}>
                  Use this patient
                </Button>
              )}
            </div>
          ))}
          <div className="small text-muted mt-2">
            If this is a different person on the same number (family member), just carry on filling the form.
          </div>
        </Alert>
      )}

      <Card className="mb-4">
        <Card.Header>
          <i className="bi bi-person-vcard me-2" />
          Basic Details
        </Card.Header>
        <Card.Body>
          <Row className="g-3">
            <Col md={6}>
              <Form.Label className="required">Patient Name</Form.Label>
              <InputGroup size="lg">
                <InputGroup.Text>
                  <i className="bi bi-person" />
                </InputGroup.Text>
                <Form.Control
                  autoFocus
                  value={form.name}
                  isInvalid={!!errors.name}
                  onChange={(e) => set('name', e.target.value)}
                  placeholder="Full name"
                />
                <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
              </InputGroup>
            </Col>

            <Col md={6}>
              <Form.Label className="required">Mobile Number</Form.Label>
              <InputGroup size="lg">
                <InputGroup.Text>
                  <i className="bi bi-telephone" />
                </InputGroup.Text>
                <Form.Control
                  value={form.mobile}
                  isInvalid={!!errors.mobile}
                  onChange={(e) => set('mobile', e.target.value.replace(/[^\d+\- ]/g, ''))}
                  placeholder="10 digit number"
                  inputMode="numeric"
                  maxLength={15}
                />
                {checking && (
                  <InputGroup.Text>
                    <Spinner animation="border" size="sm" className="text-muted" />
                  </InputGroup.Text>
                )}
                <Form.Control.Feedback type="invalid">{errors.mobile}</Form.Control.Feedback>
              </InputGroup>
              <Form.Text className="text-muted">
                We check this number against existing patients as you type.
              </Form.Text>
            </Col>

            <Col sm={6} md={3}>
              <Form.Label className="required">Gender</Form.Label>
              <Form.Select value={form.gender} isInvalid={!!errors.gender} onChange={(e) => set('gender', e.target.value)}>
                <option value="">Select</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </Form.Select>
              <Form.Control.Feedback type="invalid">{errors.gender}</Form.Control.Feedback>
            </Col>

            <Col sm={6} md={3}>
              <Form.Label>Age</Form.Label>
              <InputGroup>
                <Form.Control
                  type="number"
                  value={form.age}
                  isInvalid={!!errors.age}
                  onChange={(e) => set('age', e.target.value)}
                  placeholder="0"
                />
                <Form.Select
                  value={form.ageUnit}
                  onChange={(e) => set('ageUnit', e.target.value)}
                  style={{ maxWidth: 105 }}
                >
                  <option value="years">years</option>
                  <option value="months">months</option>
                  <option value="days">days</option>
                </Form.Select>
              </InputGroup>
              <div className="invalid-feedback d-block">{errors.age}</div>
            </Col>

            <Col sm={6} md={3}>
              <Form.Label>Weight</Form.Label>
              <InputGroup>
                <Form.Control
                  type="number"
                  step="0.1"
                  value={form.weight}
                  onChange={(e) => set('weight', e.target.value)}
                  placeholder="e.g. 68"
                />
                <InputGroup.Text>kg</InputGroup.Text>
              </InputGroup>
            </Col>

            <Col sm={6} md={3}>
              <Form.Label>Blood Group</Form.Label>
              <Form.Select value={form.bloodGroup} onChange={(e) => set('bloodGroup', e.target.value)}>
                {BLOOD_GROUPS.map((bg) => (
                  <option key={bg} value={bg}>
                    {bg || 'Not known'}
                  </option>
                ))}
              </Form.Select>
            </Col>

            {showVitals && (
              <>
                <Col sm={6} md={3}>
                  <Form.Label>Blood Pressure</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="number"
                      placeholder="120"
                      value={vitals.bpSystolic}
                      onChange={(e) => setVital('bpSystolic', e.target.value)}
                    />
                    <InputGroup.Text className="px-2">/</InputGroup.Text>
                    <Form.Control
                      type="number"
                      placeholder="80"
                      value={vitals.bpDiastolic}
                      onChange={(e) => setVital('bpDiastolic', e.target.value)}
                    />
                  </InputGroup>
                </Col>

                <Col sm={6} md={3}>
                  <Form.Label>Pulse</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="number"
                      value={vitals.pulse}
                      onChange={(e) => setVital('pulse', e.target.value)}
                    />
                    <InputGroup.Text>/min</InputGroup.Text>
                  </InputGroup>
                </Col>

                <Col sm={6} md={3}>
                  <Form.Label>Temperature</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="number"
                      step="0.1"
                      value={vitals.temperature}
                      onChange={(e) => setVital('temperature', e.target.value)}
                    />
                    <InputGroup.Text>F</InputGroup.Text>
                  </InputGroup>
                </Col>

                <Col sm={6} md={3}>
                  <Form.Label>SpO2</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="number"
                      value={vitals.spo2}
                      onChange={(e) => setVital('spo2', e.target.value)}
                    />
                    <InputGroup.Text>%</InputGroup.Text>
                  </InputGroup>
                </Col>
              </>
            )}

            <Col md={8}>
              <Form.Label>Address</Form.Label>
              <Form.Control
                value={form.address}
                onChange={(e) => set('address', e.target.value)}
                placeholder="House / street / area"
              />
            </Col>

            <Col md={4}>
              <Form.Label>City / Village</Form.Label>
              <Form.Control value={form.city} onChange={(e) => set('city', e.target.value)} />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Header>
          <i className="bi bi-clipboard2-pulse me-2" />
          Medical Background
          <span className="text-muted fw-normal small ms-2">shown to the doctor on every visit</span>
        </Card.Header>
        <Card.Body>
          <Row className="g-3">
            <Col md={6}>
              <Form.Label>
                Allergies
                <i className="bi bi-exclamation-triangle-fill text-danger ms-1" title="Highlighted for the doctor" />
              </Form.Label>
              <TagInput
                value={form.allergies}
                onChange={(v) => set('allergies', v)}
                placeholder="e.g. Penicillin - press Enter"
              />
            </Col>

            <Col md={6}>
              <Form.Label>Long-standing Conditions</Form.Label>
              <TagInput
                value={form.chronicConditions}
                onChange={(v) => set('chronicConditions', v)}
                placeholder="e.g. Diabetes - press Enter"
                suggestions={['Diabetes', 'Hypertension', 'Asthma', 'Thyroid', 'Heart Disease', 'Kidney Disease']}
              />
            </Col>

            <Col xs={12}>
              <Form.Label>Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={form.notes}
                onChange={(e) => set('notes', e.target.value)}
                placeholder="Anything else worth recording"
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Header>
          <i className="bi bi-paperclip me-2" />
          Reports &amp; Attachments
          <span className="text-muted fw-normal small ms-2">
            sonography, blood reports, x-rays &mdash; the doctor opens these during the consultation
          </span>
        </Card.Header>
        <Card.Body>
          <AttachmentField value={form.reports} onChange={(v) => set('reports', v)} />
        </Card.Body>
      </Card>

      <div className="d-flex flex-wrap gap-2 justify-content-between align-items-center">
        <div className="text-muted small">
          <span className="text-danger">*</span> marked fields are required
        </div>
        <div className="d-flex gap-2">
          {onCancel && (
            <Button variant="light" size="lg" onClick={onCancel} disabled={busy}>
              Cancel
            </Button>
          )}
          <Button type="submit" size="lg" className="btn-clinic px-4" disabled={busy}>
            {busy ? <Spinner animation="border" size="sm" className="me-2" /> : <i className="bi bi-check-lg me-2" />}
            {submitLabel}
          </Button>
        </div>
      </div>
    </Form>
  );
}
